import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import Spinner from '../components/Spinner';
import normalizeImageUrl from '../utils/normalizeImageUrl';

interface QuizQuestion {
  _id: string;
  questionText: string;
  questionType: 'multiple-choice' | 'short-answer' | 'essay';
  options?: string[];
  correctAnswerIndex?: number;
  correctAnswer?: string;
  points?: number;
  imageUrl?: string;
}

interface QuizItem {
  _id: string;
  title: string;
  description: string;
  instructions?: string;
  durationMinutes?: number;
  attemptsAllowed?: number;
  subject: { _id: string; name: string };
  className: string;
  questions: QuizQuestion[];
  totalPoints: number;
  dueDate?: string;
  startTime?: string;
  endTime?: string;
  isPublished: boolean;
  existingAttempts?: number;
  studentSubmission?: any;
}

const QuizDetail: React.FC = () => {
  const navigate = useNavigate();
  const { quizId } = useParams<{ quizId: string }>();
  const [quiz, setQuiz] = useState<QuizItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizMode, setQuizMode] = useState<'instruction' | 'taking' | 'review' | 'results'>('instruction');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [quizAnswers, setQuizAnswers] = useState<Record<string, { selectedOptionIndex?: number | null; answerText?: string }>>({});
  const [quizResult, setQuizResult] = useState<any | null>(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);

  const loadQuiz = useCallback(async (silent = false) => {
    if (!quizId) return undefined;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await api.get(`/school-work/quizzes/${quizId}`);
      const payload = response.data;
      setQuiz(payload);

      if (payload.studentSubmission) {
        setQuizResult(payload.studentSubmission);
      }

      if (payload.attemptsAllowed && payload.attemptsAllowed > 0 && payload.existingAttempts >= payload.attemptsAllowed) {
        setQuizMode('results');
      }

      return payload;
    } catch (err: any) {
      if (!silent) {
        setError(err.response?.data?.message || 'Unable to load quiz');
      }
      return undefined;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [quizId]);

  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);

  useEffect(() => {
    if (quizMode !== 'results' || !quizResult) {
      return undefined;
    }

    const refreshInterval = window.setInterval(async () => {
      const payload = await loadQuiz(true);
      if (payload?.studentSubmission) {
        const latest = payload.studentSubmission;
        const currentUpdatedAt = quizResult.updatedAt ? new Date(quizResult.updatedAt).getTime() : 0;
        const latestUpdatedAt = latest.updatedAt ? new Date(latest.updatedAt).getTime() : 0;

        if (latestUpdatedAt > currentUpdatedAt || latest.score !== quizResult.score) {
          setQuizResult(latest);
          window.clearInterval(refreshInterval);
        }
      }
    }, 5000);

    return () => window.clearInterval(refreshInterval);
  }, [quizMode, loadQuiz, quizResult]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const handleQuizAnswerChange = (questionId: string, changes: { selectedOptionIndex?: number | null; answerText?: string }) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...changes
      }
    }));
  };

  const handlePrevQuestion = () => {
    setActiveQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextQuestion = () => {
    if (!quiz) return;
    setActiveQuestionIndex((prev) => Math.min(prev + 1, quiz.questions.length - 1));
  };

  const handleReviewQuiz = () => {
    setQuizMode('review');
  };

  const handleReturnToQuiz = () => {
    setQuizMode('taking');
  };

  const formatSeconds = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.abs(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getSecondsUntil = (time?: string) => {
    if (!time) {
      return null;
    }
    return Math.ceil((new Date(time).getTime() - currentTime.getTime()) / 1000);
  };

  const getAttemptWindowSeconds = () => {
    if (!quiz || !quiz.durationMinutes) return null;
    const durationSeconds = quiz.durationMinutes * 60;
    if (quiz.endTime) {
      const secondsUntilEnd = getSecondsUntil(quiz.endTime);
      if (secondsUntilEnd === null) return durationSeconds;
      return Math.min(durationSeconds, secondsUntilEnd);
    }
    return durationSeconds;
  };

  const isBeforeStart = quiz?.startTime ? getSecondsUntil(quiz.startTime)! > 0 : false;
  const isAfterEnd = quiz?.endTime ? getSecondsUntil(quiz.endTime)! < 0 : false;

  const submittingRef = React.useRef(false);

  const handleSubmitQuiz = useCallback(async () => {
    if (!quiz || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setQuizError(null);
    setSubmittingQuiz(true);

    try {
      const payload = {
        answers: quiz.questions.map((question) => ({
          questionId: question._id,
          selectedOptionIndex: quizAnswers[question._id]?.selectedOptionIndex,
          answerText: quizAnswers[question._id]?.answerText || ''
        }))
      };

      const response = await api.post(`/school-work/quizzes/${quiz._id}/submit`, payload);
      setQuizResult(response.data);
      setQuiz((prevQuiz) => prevQuiz ? {
        ...prevQuiz,
        existingAttempts: (prevQuiz.existingAttempts || 0) + 1,
      } : prevQuiz);
      await loadQuiz();
      setQuizMode('results');
    } catch (err: any) {
      setQuizError(err.response?.data?.message || 'Unable to submit quiz');
    } finally {
      submittingRef.current = false;
      setSubmittingQuiz(false);
    }
  }, [quiz, quizAnswers, loadQuiz]);

  const startQuiz = () => {
    if (!quiz) return;
    if (quiz.attemptsAllowed && quiz.attemptsAllowed > 0 && (quiz.existingAttempts || 0) >= quiz.attemptsAllowed) {
      setQuizError(`You have reached the maximum of ${quiz.attemptsAllowed} attempt(s) for this quiz. You may only view results.`);
      return;
    }
    if (isBeforeStart) {
      setQuizError('This quiz has not started yet. Please return once the start time has passed.');
      return;
    }
    if (isAfterEnd) {
      setQuizError('This quiz has ended and can no longer be started.');
      return;
    }

    setQuizMode('taking');
    setActiveQuestionIndex(0);
    setQuizError(null);
    setQuizResult(null);
    if (quiz.durationMinutes && quiz.durationMinutes > 0) {
      const attemptWindowSeconds = getAttemptWindowSeconds();
      setTimeRemainingSeconds(attemptWindowSeconds !== null ? attemptWindowSeconds : quiz.durationMinutes * 60);
    }
  };

  useEffect(() => {
    if (quizMode === 'taking' && isAfterEnd && quiz) {
      handleSubmitQuiz();
    }
  }, [quizMode, isAfterEnd, handleSubmitQuiz, quiz]);

  // Timer effect for auto-submit
  useEffect(() => {
    if (quizMode !== 'taking' || !quiz || !quiz.durationMinutes || quiz.durationMinutes <= 0) {
      return undefined;
    }

    if (timeRemainingSeconds === null) {
      setTimeRemainingSeconds(quiz.durationMinutes * 60);
      return undefined;
    }

    const timeoutId = window.setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev === null) {
          return null;
        }
        if (prev <= 1) {
          window.clearInterval(timeoutId);
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timeoutId);
  }, [quizMode, quiz, quiz?.durationMinutes, timeRemainingSeconds, handleSubmitQuiz]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spinner />
      </Box>
    );
  }

  if (error || !quiz) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/school-work')} sx={{ mb: 2 }}>
          Back to School Work
        </Button>
        <Alert severity="error">{error || 'Quiz not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/school-work')} sx={{ mb: 3 }}>
        Back to School Work
      </Button>

      {/* Instruction Page */}
      {quizMode === 'instruction' && (
        <Box>
          <Typography variant="h5" sx={{ mb: 2 }}>{quiz.title}</Typography>
          <Typography sx={{ mb: 2 }}>{quiz.description}</Typography>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Instructions</Typography>
              <Typography sx={{ mb: 2 }}>{quiz.instructions || quiz.description || 'No instructions provided.'}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Duration:</strong> {quiz.durationMinutes && quiz.durationMinutes > 0 ? `${quiz.durationMinutes} minute${quiz.durationMinutes === 1 ? '' : 's'}` : 'No limit'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Attempts Allowed:</strong> {quiz.attemptsAllowed && quiz.attemptsAllowed > 0 ? quiz.attemptsAllowed : 'Unlimited'}
              </Typography>
              {quiz.attemptsAllowed && quiz.attemptsAllowed > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Attempts Used:</strong> {quiz.existingAttempts || 0} / {quiz.attemptsAllowed}
                </Typography>
              )}
              {quiz.startTime && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Start Time:</strong> {new Date(quiz.startTime).toLocaleString()}
                </Typography>
              )}
              {quiz.endTime && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>End Time:</strong> {new Date(quiz.endTime).toLocaleString()}
                </Typography>
              )}
              {isBeforeStart && quiz.startTime && (
                <Alert severity="info" sx={{ my: 2 }}>
                  This quiz becomes available on {new Date(quiz.startTime).toLocaleString()} ({formatSeconds(getSecondsUntil(quiz.startTime) || 0)} from now).
                </Alert>
              )}
              {isAfterEnd && quiz.endTime && (
                <Alert severity="warning" sx={{ my: 2 }}>
                  This quiz ended on {new Date(quiz.endTime).toLocaleString()} and can no longer be started.
                </Alert>
              )}
              {quiz.dueDate ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Due Date:</strong> {new Date(quiz.dueDate).toLocaleDateString()}
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
                {(quiz.attemptsAllowed && quiz.attemptsAllowed > 0 && (quiz.existingAttempts || 0) >= quiz.attemptsAllowed) ? (
                  <Button variant="contained" onClick={() => setQuizMode('results')}>
                    View Results
                  </Button>
                ) : (
                  <Button variant="contained" onClick={startQuiz} disabled={isBeforeStart || isAfterEnd}>
                    Start Quiz
                  </Button>
                )}
                <Button variant="outlined" onClick={() => navigate('/school-work')}>
                  Close
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Taking Quiz Page */}
      {quizMode === 'taking' && quiz.questions.length > 0 && (
        <Box>
          {quizError && <Alert severity="error" sx={{ mb: 2 }}>{quizError}</Alert>}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Question {activeQuestionIndex + 1} of {quiz.questions.length}
          </Typography>
          {quiz.durationMinutes && quiz.durationMinutes > 0 && (
            <Typography variant="body2" sx={{ mb: 2, color: 'primary.main' }}>
              Time Remaining: {timeRemainingSeconds !== null ? formatSeconds(timeRemainingSeconds) : 'Calculating...'}
            </Typography>
          )}

          {(() => {
            const question = quiz.questions[activeQuestionIndex];
            return (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Question {activeQuestionIndex + 1} • {question.points || 1} point{(question.points || 1) !== 1 ? 's' : ''}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>{question.questionText}</Typography>
                  {question.imageUrl && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <img src={normalizeImageUrl(question.imageUrl) || question.imageUrl} alt="Question Graphic" style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }} />
                    </Box>
                  )}

                  {question.questionType === 'multiple-choice' && (
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={quizAnswers[question._id]?.selectedOptionIndex !== undefined && quizAnswers[question._id]?.selectedOptionIndex !== null
                          ? String(quizAnswers[question._id]?.selectedOptionIndex)
                          : ''}
                        onChange={(e) => handleQuizAnswerChange(question._id, { selectedOptionIndex: parseInt(e.target.value, 10) })}
                      >
                        {question.options?.map((option, optionIndex) => (
                          <FormControlLabel
                            key={optionIndex}
                            value={String(optionIndex)}
                            control={<Radio />}
                            label={option || `Option ${optionIndex + 1}`}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}

                  {question.questionType === 'short-answer' && (
                    <TextField
                      label="Your Answer"
                      fullWidth
                      multiline
                      rows={3}
                      value={quizAnswers[question._id]?.answerText || ''}
                      onChange={(e) => handleQuizAnswerChange(question._id, { answerText: e.target.value })}
                      margin="normal"
                    />
                  )}

                  {question.questionType === 'essay' && (
                    <TextField
                      label="Your Essay Response"
                      fullWidth
                      multiline
                      rows={5}
                      value={quizAnswers[question._id]?.answerText || ''}
                      onChange={(e) => handleQuizAnswerChange(question._id, { answerText: e.target.value })}
                      margin="normal"
                    />
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" disabled={activeQuestionIndex === 0} onClick={handlePrevQuestion}>
              Previous
            </Button>
            {activeQuestionIndex < quiz.questions.length - 1 ? (
              <Button variant="contained" onClick={handleNextQuestion}>
                Next
              </Button>
            ) : (
              <Button variant="contained" onClick={handleReviewQuiz}>
                Review
              </Button>
            )}
            <Button variant="text" onClick={() => navigate('/school-work')}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Review Page */}
      {quizMode === 'review' && quiz && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Quiz Review</Typography>
          {(() => {
            const attempted = quiz.questions.reduce((count, question) => {
              const answer = quizAnswers[question._id];
              const hasAnswer = answer?.selectedOptionIndex !== undefined && answer?.selectedOptionIndex !== null
                ? true
                : Boolean(answer?.answerText?.trim());
              return count + (hasAnswer ? 1 : 0);
            }, 0);
            const total = quiz.questions.length;
            const notAttempted = total - attempted;
            return (
              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: 'green' }}>
                  Questions attempted: {attempted}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Questions not attempted: {notAttempted}
                </Typography>
              </Box>
            );
          })()}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" onClick={handleReturnToQuiz}>
              Go back to Quiz
            </Button>
            <Button variant="contained" disabled={submittingQuiz} onClick={handleSubmitQuiz}>
              Submit
            </Button>
            <Button variant="text" onClick={() => navigate('/school-work')}>
              Close
            </Button>
          </Box>
        </Box>
      )}

      {/* Results Page */}
      {quizMode === 'results' && quizResult && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Quiz Results</Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Score: {quizResult.score} / {quizResult.totalPoints}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {quizResult.status === 'graded'
              ? 'This quiz was graded automatically.'
              : 'This quiz requires manual review for essay responses.'}
          </Typography>
          {quizResult.answers?.map((answer: any, idx: number) => (
            <Card key={idx} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Question {idx + 1}
                </Typography>
                <Typography sx={{ mb: 1 }}>{answer.questionText}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your Answer: {answer.questionType === 'multiple-choice'
                    ? answer.selectedOptionText || 'Not answered'
                    : answer.answerText || 'Not answered'}
                </Typography>
                {answer.correctAnswerText && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Correct Answer: {answer.correctAnswerText}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Points: {answer.pointsAwarded} / {answer.maxPoints}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {answer.feedback}
                </Typography>
              </CardContent>
            </Card>
          ))}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/school-work')}>
              Back to School Work
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QuizDetail;
